"use client";

import { useCallback, useState } from "react";
import dynamic from "next/dynamic";
import { GoogleAccentPinMarker } from "@/components/Molecules/AccentPinMarker";
import { LocationMapDrawer } from "@/components/Molecules/LocationMapDrawer";
import { MapComponent } from "@/components/Molecules/MapComponent";
import {
	getLocationMapHeight,
	resolveMapPinAccent,
	resolveMapStyleAccent,
	LOCATION_MAP_ZOOM,
	type MapAccentColor,
	type MapIconAsset,
} from "@/lib/map-config";
import type { OpenStreetMapComponentProps } from "@/components/Molecules/OpenStreetMapComponent";
import styles from "./LocationMapView.module.scss";

const OpenStreetMapComponent = dynamic<OpenStreetMapComponentProps>(
	() =>
		import("@/components/Molecules/OpenStreetMapComponent").then(
			(mod) => mod.OpenStreetMapComponent
		),
	{ ssr: false }
);

type LocationMapViewProps = {
	id: string;
	locationType?: string | null;
	location: { latitude: number; longitude: number };
	mapIcon?: { img?: MapIconAsset | null } | null;
	mapAccentColor?: MapAccentColor | null;
	mapStylePreset?: string | null;
	mapTileStyle?: string | null;
	sectionFontColor?: string | null;
	width: number;
};

export function LocationMapView({
	id,
	locationType,
	location,
	mapIcon,
	mapAccentColor,
	mapStylePreset,
	mapTileStyle,
	sectionFontColor,
	width,
}: LocationMapViewProps) {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const center = {
		lat: location.latitude,
		lng: location.longitude,
	};
	const iconAsset = mapIcon?.img ?? null;
	const pinAccentColor = resolveMapPinAccent(mapAccentColor, sectionFontColor);
	const mapStyleAccent = resolveMapStyleAccent(
		mapAccentColor,
		sectionFontColor,
		mapStylePreset
	);
	const mapHeight = getLocationMapHeight(width);

	const toggleDrawer = useCallback(() => {
		setDrawerOpen((open) => !open);
	}, []);

	return (
		<div className={styles.wrapper} style={{ height: mapHeight }}>
			{locationType === "open" ? (
				<OpenStreetMapComponent
					width={width}
					center={center}
					mapIcon={iconAsset}
					accentColor={mapStyleAccent}
					pinAccentColor={pinAccentColor}
					tileStyle={mapTileStyle}
					onPinClick={toggleDrawer}
				/>
			) : (
				<MapComponent
					width={width}
					height={mapHeight}
					key={id}
					variant="location"
					fixedZoom={LOCATION_MAP_ZOOM}
					defaultCenter={center}
					markers={[location]}
					mapStylePreset={mapStylePreset}
					accentColor={mapStyleAccent}
				>
					<GoogleAccentPinMarker
						position={center}
						mapIcon={iconAsset}
						accentColor={pinAccentColor}
						onClick={toggleDrawer}
					/>
				</MapComponent>
			)}

			<LocationMapDrawer
				open={drawerOpen}
				lat={center.lat}
				lng={center.lng}
				accentColor={pinAccentColor}
			/>
		</div>
	);
}
